import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { FUNCTIONAL_REACH_ASSESSMENT } from "../models/Enums";
import {
  API_URL,
  AromMenuOptions,
  ASSESSMENT_STATUSES,
  AWS_CONFIG,
  DL_MODELS,
  S3_BUCKET,
} from "../movenet/params";
import * as Sentry from "@sentry/browser";
import {
  FunctionalReachAudios,
  FunctionalReachStates,
} from "./assessment_states";
import { FunctionalReachAudioPlayer } from "./audio-player";
import { config } from "./env_config";
import { MoveNetPoseWrapper } from "./pose-wrapper";
import { MultiPoseUtility } from "./multipose_utility";
import { PatientSelection } from "./patient_selection";
import {
  isArmStretched,
  recordForwardReachDistance,
  recordLeftReachDistance,
  recordRightReachDistance,
  isPatientInProfilePosition,
  getStretchedArmForForwardReach,
  getKeypointIndices,
} from "./record_FuntionalReach_Distance";
import { SentryLogging } from "./sending_sentry_logs";
import { SitStandProcessing } from "./sitStandProcessing";
import { GestureDetector } from "./start_gesture_detector";
import {
  startiOSVoiceListening,
  stopiOSVoiceListening,
} from "../utils/mobile-bridge";

const time_per_assessment_in_sec = 11;
const s3Client = new S3Client(AWS_CONFIG);

export const reach_distance_metric = {
  leftReach: 0,
  rightReach: 0,
  forwardReach: 0,
};

export class FunctionalReachAssessment {
  /**
   * Initializes the Upper Body AROM assessment with necessary callbacks,
   * UI elements, and default state values.
   * @param {Object} camera - Camera interface for video input and drawing.
   * @param {Function} switch_to_single_pose - Callback to switch to single-pose model.
   * @param {Function} reset_pose_estimator - Callback to reset pose estimator.
   * @param {Function} switch_to_multi_pose - Callback to switch back to multi-pose model.
   */
  constructor(
    calibration,
    camera,
    switch_to_single_pose,
    reset_pose_estimator,
    switch_to_multi_pose,
  ) {
    this.switch_to_single_pose_callback = switch_to_single_pose;
    this.switch_to_multi_pose_callback = switch_to_multi_pose;
    this.reset_pose_estimator = reset_pose_estimator;
    this.camera = camera;
    this.calibration = calibration;
    this.current_state = FunctionalReachStates.begin_assessment;
    this.gesture_detector = new GestureDetector();
    this.sit_stand_processing = new SitStandProcessing();
    this.end_time = 0;
    this.first_frame = true;
    this.audio_player = new FunctionalReachAudioPlayer();
    this.return_to_start_audio = new Audio("audios/gait/return_to_start_area.mp3"); // Reuse gait audio
    this.therapist_id = -1;
    this.patient_id = -1;
    this.patient_ID_changed = false;
    this.track_patient = false;
    this.start_time = 0;
    this.is_calibrated = false;
    this.feet_box_x_min = 0;
    this.feet_box_x_max = 0;
    this.feet_box_y_min = 0;
    this.feet_box_y_max = 0;
    this.frames_with_no_patient = 0;
    this.frames_with_patient_outside_extended_box = 0;
    this.return_to_start_modal_shown = false; // Track if return-to-start modal is currently showing
    this.insetoutputCanvas = document.getElementById("insetoutput");
    this.are_you_there_flag = true;
    this.are_you_there_timer = 0;
    this.current_time = 0;
    this.first_frame_calibration_2 = true;
    this.time_calibration_2 = 0;
    this.inactive_for_60_seconds = false;
    this.start_timer_reference = 0;
    this.within_bound_start_flag = false;
    this.assessment_timer = 0;
    this.first_frame_time = 0;

    this.angle_value = -1;
    this.patient_within_box_check = false;
    this.patient_within_box_timer = 0;
    this.current_assessment_phase = FUNCTIONAL_REACH_ASSESSMENT.Left_Reach;
    this.arom_left_high_text = document.getElementById("arom-left-high-text");
    this.arom_left_high_value = document.getElementById("arom-left-high-value");
    this.arom_left_low_text = document.getElementById("arom-left-low-text");
    this.arom_left_low_value = document.getElementById("arom-left-low-value");
    this.arom_right_high_text = document.getElementById("arom-right-high-text");
    this.arom_right_high_value = document.getElementById(
      "arom-right-high-value",
    );
    this.arom_right_low_text = document.getElementById("arom-right-low-text");
    this.arom_right_low_value = document.getElementById("arom-right-low-value");
    this.arom_realtime_angle_left = document.getElementById(
      "arom-realtime-angle-left",
    );
    this.arom_realtime_angle_right = document.getElementById(
      "arom-realtime-angle-right",
    );
    this.arom_assessment_counter_left = document.getElementById(
      "arom-realtime-angle-left-value",
    );
    this.arom_assessment_counter_right = document.getElementById(
      "arom-realtime-angle-right-value",
    );
    let videoWidth = 1080;
    let videoHeight = 1920;
    this.options = {
      sx: 0,
      sy: 0,
      sw: videoWidth,
      sh: videoHeight,
    };
    this.funtionalReach_retry = document.getElementById("funtionalReach-retry");
    this.display_modalFaceCamera = false;
    this.play_beep_sound = true;
    this.assessment_function = null;
    if (this.insetoutputCanvas) {
      this.insetoutputCanvas.style.display = "none";
    }
    let patientServerID = localStorage.getItem("patientServerID")
      ? localStorage.getItem("patientServerID")
      : "000";
    let userID = localStorage.getItem("UserID")
      ? localStorage.getItem("UserID")
      : "000";
    let businessCode = localStorage.getItem("BusinessCode")
      ? localStorage.getItem("BusinessCode")
      : "000";

    Sentry.setTag("UserID", userID);
    Sentry.setTag("Patient Server ID", patientServerID);
    Sentry.setTag("Business Code", businessCode);

    this.sentry_prefix = `FunctionalReach_` + new Date().toISOString();
    ASSESSMENT_STATUSES.sentry_message = this.sentry_prefix;
    ASSESSMENT_STATUSES.are_you_there = false;
    ASSESSMENT_STATUSES.incomplete_test = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;
    ASSESSMENT_STATUSES.assessment_successful_completion = false;
    ASSESSMENT_STATUSES.assessment_error = false;
    this.sentry_start_time = 0;
    this.are_you_there_modal_timer =
      config["are-you-there-timer-in-seconds"] * 1000;

    this.left_reference_point = null;
    this.right_reference_point = null;

    // Forward reach uses TWO separate references (allows arm switching during assessment)
    this.forward_left_reference_point = null;   // Captured when left arm stretched
    this.forward_right_reference_point = null;  // Captured when right arm stretched

    // Depth used for reach distance calculations
    this.averaged_depth = 0;

    // Flags to prevent repeated logging in states that run every frame
    this.pre_assessment_logged = false;
    this.assessment_started_logged = false;
    this.depth_calculation_done = false;

    this.is_api_called = false;

    // LIVE CAMERA: Initialize cleanup flag
    this.cleanup_called = false;

    // Baseline shoulder width for forward reach profile detection
    this.baseline_shoulder_width = null;

    // Offscreen canvas for capturing frames to pass to the depth model
    let canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    this.offscreen_canvas = canvas;
    this.offscreen_canvas_ctx = this.offscreen_canvas.getContext("2d", {
      willReadFrequently: true,
    });
    this.canvas = document.getElementById("output");
    this.ctx = this.canvas.getContext("2d");
    this.side_margin = 0.4;

    this.enabled_voice_listener = false;
    this.voice_command_received = false;

    // Remote NEXT: reaches skipped (reported as skipped on each phase) and reaches measured to the end
    this.skipped_phases = [];
    this.completed_phase_count = 0;
    // Remote REWIND: the phase that most recently completed or was skipped, while it can still be
    // redone (until the next phase's start is accepted); null otherwise
    this.rewind_target = null;

    // Voice command event listener (5-second timeout with gesture fallback)
    window.addEventListener("voice-command", (event) => {
      const { command } = event.detail;
      if (command === "START") {
        this.voice_command_received = true;
        this.enabled_voice_listener = false;
        // Stop immediately when voice command received
        try {
          stopiOSVoiceListening();
        } catch (e) {
          this.append_sentry_message("Error stopping voice listening: " + e.message);
        }
      }
    });

    // Expose instance globally for retry functionality
    window.functionalReach = this;

    // Remote UP/DOWN patient selection: the participant the therapist selects before each reach.
    // The selection is kept for the later reaches.
    this.patient_selection = new PatientSelection();

  }

  /**
   * Main update loop called on each video frame. Manages state transitions,
   * UI interactions, and calls to specific assessment logic.
   * @param {Array} poses - Array of detected poses from MoveNet.
   */
  update(poses) {
    this.current_time = Date.now();

    // The participant the therapist selected with the remote, while it can still be changed.
    // patient_id also makes movenet draw this person's skeleton green, and detect_patient prefers
    // this person (by id). Without a selection patient_id is left as it was.
    if (this.is_patient_selection_open()) {
      const patient = this.patient_selection.update(poses);
      if (patient) this.patient_id = patient.id;
    }

    this.restart_phase_feature();
    this.skip_phase_feature();

    // --------------------------- TERMINATION CONDITIONS --------------------------------
    if (
      this.current_state === FunctionalReachStates.test_complete ||
      ASSESSMENT_STATUSES.calibration_error_state === true ||
      this.current_state === FunctionalReachStates.assessment_interrupted ||
      ASSESSMENT_STATUSES.exit_assessment_modal === true
    ) {
      // Stop voice listening if active
      if (this.enabled_voice_listener) {
        try {
          stopiOSVoiceListening();
        } catch (e) {
          this.append_sentry_message("Error stopping voice listening: " + e.message);
        }
        this.enabled_voice_listener = false;
      }

      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;

      // LIVE CAMERA: Use single cleanup function for comprehensive UI cleanup
      if (!this.cleanup_called) {
        this.cleanupUI();
        this.cleanup_called = true; // Prevent multiple cleanup calls
      }

      return;
    }

    // Handle inactivity prompt ("Are you there?")
    if (!poses || poses.length === 0) {
      if (this.are_you_there_flag) {
        this.are_you_there_flag = false;
        this.are_you_there_timer = Date.now();
      }
      const elapsed = this.current_time - this.are_you_there_timer;
      if (elapsed >= 60000 && !this.inactive_for_60_seconds) {
        this.inactive_for_60_seconds = true;
        this.append_sentry_message(
          "No pose/activity detected for more than 60 seconds.",
        );
      }
      if (elapsed >= this.are_you_there_modal_timer) {
        // Stop voice listening if active (safety measure)
        if (this.enabled_voice_listener) {
          try {
            stopiOSVoiceListening();
          } catch (e) {
            this.append_sentry_message("Error stopping voice listening: " + e.message);
          }
          this.enabled_voice_listener = false;
        }

        this.are_you_there_flag = true;
        this.are_you_there_timer = 0;
        document.getElementById("hdnAreYouTherePopup").click();
      }
    } else {
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
    }

    if (document.readyState !== "complete") return;

    if (this.first_frame) {
      // --------------------------- INITIAL CALIBRATION --------------------------------
      reach_distance_metric.leftReach = 0;
      reach_distance_metric.rightReach = 0;
      reach_distance_metric.forwardReach = 0;

      sessionStorage.setItem("phaseNo", "1");
      // LIVE CAMERA: Start with left reach (original flow for all reach types)
      sessionStorage.setItem(
        "functionalReachAssessmentIndex",
        FUNCTIONAL_REACH_ASSESSMENT.Left_Reach,
      );
      this.current_assessment_phase = FUNCTIONAL_REACH_ASSESSMENT.Left_Reach;
      this.assessment_function = (poses) =>
        recordLeftReachDistance(this, poses);

      this.first_frame = false;

      // Stop any voice listening before playing audio (prevents iOS ducking on retry)
      try {
        stopiOSVoiceListening();
      } catch (e) {
        // Ignore if nothing was running
      }
      this.enabled_voice_listener = false;

      this.audio_player.play(
        FunctionalReachAudios.participant_should_stand_with_in_the_starting_box,
      );
      document.getElementById("hdnAssessmentStartUpMessage").click();
      this.camera.flip_horizontal();

      // Log initial DL model load status and screen resolution
      if (DL_MODELS.MessageForSentry === "") {
        this.append_sentry_message("DL_Model already loaded");
      } else {
        this.append_sentry_message(DL_MODELS.MessageForSentry);
        DL_MODELS.MessageForSentry = "";
      }
      this.append_sentry_message(
        "Screen Resolution: " +
          this.sit_stand_processing.canvas.width +
          "x" +
          this.sit_stand_processing.canvas.height,
      );
      this.first_frame_time = this.current_time;
    }

    // Skip error check after all assessments complete, and during phase 3
    // post-assessment modal (patient can move freely after final phase ends)
    if (this.track_patient &&
      this.current_state !== FunctionalReachStates.all_assessments_complete &&
      !(this.current_state === FunctionalReachStates.post_assessment_modal &&
        this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach)) {
      this.assessment_error_check(poses);
    }

    try {
      switch (this.current_state) {
        // --------------------------- 1) BEGIN ASSESSMENT --------------------------------
        case FunctionalReachStates.begin_assessment:
          if (!this.is_api_called) {
            // Set start_time when functional reach assessment begins (patient asked to stand in box)
            if (this.start_time === 0) {
              this.start_time = this.current_time;
            }

            // Capture baseline shoulder width from first frame (frontal position)
            this.captureBaselineShoulderWidth(poses, "begin_assessment");

            this.uploadImageToS3AndCallAPI();
            this.is_api_called = true;
          }

          this.sit_stand_processing.draw_patient_box();
          if (
            // With a remote selection, only the selected person counts; otherwise anyone in the box
            this.are_within_bbox(
              this.patient_selection.start_check_poses(poses),
              this.sit_stand_processing.patient_box
            )
          ) {
            this.gesture_detector._screen_color_change();
            if (!this.patient_within_box_check) {
              this.patient_within_box_check = true;
              this.patient_within_box_timer = this.current_time;
            }
            if (this.current_time - this.patient_within_box_timer >= 3000) {
              this.within_bound_start_flag = true;
            }
          } else {
            this.within_bound_start_flag = false;
            this.patient_within_box_check = false;
            this.patient_within_box_timer = this.current_time;
            return;
          }
          if (
            !this.audio_player.is_available() ||
            this.current_time - this.first_frame_time <= 7000
          ) {
            return;
          }
          if (this.within_bound_start_flag) {
            this.audio_player.play(FunctionalReachAudios.participant_detected);
            document.getElementById("hdnParticiantWithinBox").click();
            this.append_sentry_message(
              "Participant is within the Bounding box",
            );
            this.within_bound_start_flag = false;
            this.assessment_timer = this.current_time;
            this.current_state = FunctionalReachStates.calibration_1;
            return;
          }
          break;

        // --------------------------- 7) CALIBRATION PHASE 3 --------------------------------
        case FunctionalReachStates.readyState:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 1000
          ) {
            document.getElementById("hdnAromTherapistReadyMessage").click();
            this.audio_player.play(
              FunctionalReachAudios.readyState,
              this.current_assessment_phase,
            );
            this.current_state = FunctionalReachStates.raiseHand;
            this.assessment_timer = this.current_time;
          }
          break;

        // --------------------------- 8) CALIBRATION PHASE 4 --------------------------------
        case FunctionalReachStates.raiseHand:
          // Draw patient box for visual reference
          this.sit_stand_processing.draw_patient_box();

          // Check if patient is still in box
          // With a remote selection, only the selected person counts; otherwise anyone in the box
          if (
            !this.are_within_bbox(
              this.patient_selection.start_check_poses(poses),
              this.sit_stand_processing.patient_box
            )
          ) {
            this.frames_with_no_patient++;

            // If patient out of box for ~10 frames, show return-to-start modal
            if (this.frames_with_no_patient >= 10) {
              // Show return-to-start modal (only once, not every frame)
              if (!this.return_to_start_modal_shown) {
                this.return_to_start_modal_shown = true;

                // Stop currently playing audio and mark as available
                if (this.audio_player.current_playing_audio) {
                  this.audio_player.current_playing_audio.pause();
                  this.audio_player.current_playing_audio.currentTime = 0;
                  this.audio_player.current_playing_audio = null; // Mark as available
                }

                // Stop voice listening if active
                if (this.enabled_voice_listener) {
                  try {
                    stopiOSVoiceListening();
                  } catch (e) {
                    this.append_sentry_message("Error stopping voice listening: " + e.message);
                  }
                  this.enabled_voice_listener = false;
                  // Delay audio to allow voice listening to stop
                  setTimeout(() => {
                    this.return_to_start_audio.play();
                  }, 300);
                } else {
                  this.return_to_start_audio.play();
                }

                // Hide persistent icon
                const hideIconBtn = document.getElementById("hdnHideHandRaisePersistentIcon");
                if (hideIconBtn) {
                  hideIconBtn.click();
                }

                // Show return-to-start modal
                document.getElementById("hdnReturnToStartModalPopup").click();

                this.append_sentry_message("Patient left box during raiseHand - showing return-to-start modal");
              }
              // Modal stays visible until patient returns to box
              return;
            }
          } else {
            // Patient is back in box
            if (this.return_to_start_modal_shown) {
              // Close return-to-start modal
              document.getElementById("hdnCloseReturnToStartModal").click();
              this.return_to_start_modal_shown = false;

              // Show hand raise icon again
              document.getElementById("hdnShowHandRaisePersistentIcon").click();

              this.append_sentry_message("Patient returned to box - closing return-to-start modal, showing hand raise icon");
            }
            // Reset counter
            this.frames_with_no_patient = 0;
          }

          if (
            (this.audio_player.is_available() &&
              this.current_time - this.assessment_timer > 3000) ||
            // The remote's START doesn't wait for the prompt or the settle time (it cut the prompt short)
            this.voice_command_received
          ) {
            // FALLBACK: Capture baseline shoulder width if not already captured
            this.captureBaselineShoulderWidth(poses, "raiseHand_fallback");

            // Removed raiseHand depth capture - using original calculation + 2 pre_assessment captures

            this.gesture_detector.detect_start_gesture(poses);

            if (!this.enabled_voice_listener && !this.voice_command_received) {
              // Stop any previous voice listening session before starting (like TUG)
              try {
                stopiOSVoiceListening();
              } catch (e) {
                // Ignore errors if nothing was running
              }

              setTimeout(() => {
                startiOSVoiceListening();
              }, 100);
              this.enabled_voice_listener = true;

              // Auto-stop after 5 seconds, then fall back to gesture detection
              setTimeout(() => {
                if (this.enabled_voice_listener) {
                  try {
                    stopiOSVoiceListening();
                  } catch (e) {
                    this.append_sentry_message("Error stopping voice listening: " + e.message);
                  }
                  this.enabled_voice_listener = false;
                }
              }, 5000);
            }

            if (this.gesture_detector.start_gesture_issued()  || this.voice_command_received ) {
              // Stop voice listening if gesture or voice command detected
              if (this.enabled_voice_listener) {
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }

              this.therapist_id = this.gesture_detector.gesture_issued_by;
              this.gesture_detector.reset();
              // this.append_sentry_message("Calibration Completed.");
              this.append_sentry_message(
                "Number of persons in the frame while gesture detection: " +
                  poses.length,
              );

              // Switch to BlazePose for better single-person tracking
              this.append_sentry_message("Switching to BlazePose for reach assessment...");
              this.switch_to_blazepose(poses);

              // COMMENTING OUT VOICE INTEGRATION FOR DEBUGGING
              this.voice_command_received = false;

              // Set flag synchronously so timeout guard prevents icon from showing
              ASSESSMENT_STATUSES.start_detected = true;
              ASSESSMENT_STATUSES.in_ready_phase = false;

              // Hide persistent hand raise icon immediately when hand raise detected
              document.getElementById("hdnHideHandRaisePersistentIcon")?.click();

              this.rewind_target = null; // started: REWIND can no longer go back
              this.current_state = FunctionalReachStates.go_state;
            }
          }
          break;

        // --------------------------- 4) CALIBRATION PHASE 1 --------------------------------
        case FunctionalReachStates.calibration_1:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 3000 &&
            !this.display_modalFaceCamera
          ) {
            this.display_modalFaceCamera = true;
            document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
            this.audio_player.play(
              FunctionalReachAudios.calibration_1,
              this.current_assessment_phase,
            );
            this.current_state = FunctionalReachStates.hand_orientation;
            this.assessment_timer = this.current_time;
          }

          break;

        // --------------------------- 5) HAND ORIENTATION --------------------------------
        case FunctionalReachStates.hand_orientation:
          this.display_modalFaceCamera = false;
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 2000
          ) {
            document
              .getElementById("hdnAromAssessmentHandOrientationMessage")
              .click();
            this.audio_player.play(
              FunctionalReachAudios.hand_orientation,
              this.current_assessment_phase,
            );
            this.current_state = FunctionalReachStates.hand_orientation_2;
            this.assessment_timer = this.current_time;
          }
          break;

        // --------------------------- 6) CALIBRATION PHASE 2 --------------------------------
        case FunctionalReachStates.hand_orientation_2:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 2000
          ) {
            if (this.first_frame_calibration_2) {
              document.getElementById("hdnAromAssessmentGifMessage").click();
              this.first_frame_calibration_2 = false;
              this.time_calibration_2 = this.current_time;
            }
            this.first_frame_calibration_2 = true;
            this.audio_player.play(
              FunctionalReachAudios.hand_orientation_2,
              this.current_assessment_phase,
            );
            this.current_state = FunctionalReachStates.readyState;
            this.assessment_timer = this.current_time;
          }
          break;

        case FunctionalReachStates.go_state:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 3000
          ) {
            document.getElementById("hdnAromPhase1StartMessageClose").click();
            document.getElementById("hdnAromPhase1StartMessage").click();
            this.audio_player.play(
              FunctionalReachAudios.you_have_10_seconds_to_perform_this_movement,
            );

            this.assessment_timer = this.current_time;

            // Show realtime angle display immediately
            if (
              [FUNCTIONAL_REACH_ASSESSMENT.Left_Reach].includes(
                this.current_assessment_phase
              )
            ) {
              this.arom_realtime_angle_left.style.display = "flex";
            }
            if (
              [
                FUNCTIONAL_REACH_ASSESSMENT.Right_Reach,
                FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
              ].includes(this.current_assessment_phase)
            ) {
              this.arom_realtime_angle_right.style.display = "flex";
            }

            this.current_state = FunctionalReachStates.pre_assessment;
            this.track_patient = true; // Enable foot movement monitoring
          }

          break;

        // --------------------------- 9) PRE-ASSESSMENT --------------------------------
        case FunctionalReachStates.pre_assessment:
          // Log entry only once, not every frame
          if (!this.pre_assessment_logged) {
            // this.append_sentry_message(`Entered pre_assessment state for phase: ${this.current_assessment_phase}`);
            this.pre_assessment_logged = true;

            // Hide persistent icon when entering pre_assessment (in case hand raise wasn't detected)
            ASSESSMENT_STATUSES.start_detected = true;
            ASSESSMENT_STATUSES.in_ready_phase = false;
            document.getElementById("hdnHideHandRaisePersistentIcon")?.click();
          }

          // Check if patient is still in box (use extended_patient_box like assessment_started)
          if (!this.are_within_bbox(poses, this.sit_stand_processing.extended_patient_box)) {
            this.frames_with_no_patient++;

            if (this.frames_with_no_patient >= 10) {
              // Set error flag FIRST
              ASSESSMENT_STATUSES.assessment_error = true;
              ASSESSMENT_STATUSES.assessment_Completed = true;

              // THEN hide persistent icon
              const hideIconBtn = document.getElementById("hdnHideHandRaisePersistentIcon");
              if (hideIconBtn) {
                hideIconBtn.click();
              }

              this.audio_player.play(FunctionalReachAudios.assessment_interrupted);
              this.append_sentry_message("Assessment interrupted - patient left the box during pre_assessment state");
              this.handleAssessmentInterruption();
              return;
            }
          } else {
            // Patient is in box, reset counter
            this.frames_with_no_patient = 0;
          }

          // Check if calibration data is ready first
          // Get correct ankle index based on model (BlazePose has 33 keypoints, MoveNet has 17)
          const isSinglePoseCheck = poses && poses.length > 0 && poses[0].keypoints && poses[0].keypoints.length > 20;
          const ankleIdxCheck = isSinglePoseCheck ? 28 : 16;

          let calibrationReady = false;
          if (poses && poses.length > 0 && poses[0].keypoints && poses[0].keypoints[ankleIdxCheck]) {
            const testDepth = this.calibration.get_depth_distance_inches(
              poses[0].keypoints[ankleIdxCheck].x,
              poses[0].keypoints[ankleIdxCheck].y,
              false,  // Use correct depth mapping (y >= 1000)
              true    // Apply calibration factor
            );
            calibrationReady = testDepth > 0;
          }

          // LIVE CAMERA: Use original arm stretch detection for reference capture
          if (
            !this.left_reference_point ||
            !this.right_reference_point ||
            !this.forward_reference_point
          ) {
            const armStretched = isArmStretched(poses, this.current_assessment_phase);
            if (armStretched) {
              if (
                !this.left_reference_point &&
                this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Left_Reach
              ) {
                this.left_reference_point = this.getReferencePoint(poses, FUNCTIONAL_REACH_ASSESSMENT.Left_Reach);
                this.append_sentry_message(`Left reference point captured: ${JSON.stringify(this.left_reference_point)}`);
              } else if (
                !this.right_reference_point &&
                this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Right_Reach
              ) {
                this.right_reference_point = this.getReferencePoint(poses, FUNCTIONAL_REACH_ASSESSMENT.Right_Reach);
                this.append_sentry_message(`Right reference point captured: ${JSON.stringify(this.right_reference_point)}`);
              } else if (
                this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach
              ) {
                const keypoints = poses[0].keypoints;
                const isInProfile = isPatientInProfilePosition(keypoints, this.baseline_shoulder_width);

                if (isInProfile) {
                  const armStatus = getStretchedArmForForwardReach(poses);

                  // Priority: If BOTH arms stretched, capture LEFT first (then skip right)
                  if (armStatus.left && armStatus.right) {
                    if (!this.forward_left_reference_point) {
                      this.forward_left_reference_point = this.getReferencePoint(
                        poses,
                        FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
                        'left'
                      );
                      this.append_sentry_message(`Forward LEFT reference captured (both arms stretched, left priority)`);
                    }
                  } else {
                    // Only one arm stretched - capture whichever one it is
                    if (armStatus.left && !this.forward_left_reference_point) {
                      this.forward_left_reference_point = this.getReferencePoint(
                        poses,
                        FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
                        'left'
                      );
                      this.append_sentry_message(`Forward LEFT reference captured`);
                    }

                    if (armStatus.right && !this.forward_right_reference_point) {
                      this.forward_right_reference_point = this.getReferencePoint(
                        poses,
                        FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
                        'right'
                      );
                      this.append_sentry_message(`Forward RIGHT reference captured`);
                    }
                  }
                }
              }
            }
          }

          // Proceed to assessment when audio player is available (no depth averaging needed)
          if (this.audio_player.is_available() && this.depth_calculation_done) {
            this.assessment_timer = this.current_time;
            AromMenuOptions.actualAssessmentStartTime = this.current_time;
            this.current_state = FunctionalReachStates.assessment_started;
            // document.getElementById("hdnAromPhase1StartMessageClose").click();
            // LIVE CAMERA: Re-enable countdown slider for timer display
            document.getElementById("btn-arom-assessment-counter").click();
          }
          // Calculate depth only once, not every frame
          // Get correct ankle index based on model (BlazePose has 33 keypoints, MoveNet has 17)
          const isSinglePose = poses && poses.length > 0 && poses[0].keypoints && poses[0].keypoints.length > 20;
          const rightAnkleIdx = isSinglePose ? 28 : 16;

          if (!this.depth_calculation_done && poses && poses.length > 0 && poses[0].keypoints && poses[0].keypoints[rightAnkleIdx]) {
            const ankleX = Math.round(poses[0].keypoints[rightAnkleIdx].x);
            const ankleY = Math.round(poses[0].keypoints[rightAnkleIdx].y);

            // Check depth data availability
            // const depthAvailable = this.calibration.depthData && this.calibration.depthData.data;
            // const depthShape = depthAvailable ? `${this.calibration.depthData.shape[0]}x${this.calibration.depthData.shape[1]}` : 'none';
            // this.append_sentry_message(`Depth calculation: ankle at (${ankleX}, ${ankleY}), depth data: ${depthShape}`);

            // Calculate adjusted Y for logging
            // const adjustedY = ankleY >= 1000 ? ankleY - 1000 : -1;
            // this.append_sentry_message(`Depth lookup: y=${ankleY}, adjustedY=${adjustedY}, valid=${ankleY >= 1000 && adjustedY < 920}`);

            // First get RAW depth (without calibration) to calculate calibration factor
            const rawDepth = this.calibration.get_depth_distance_inches(
              ankleX,
              ankleY,
              false,  // Use correct depth mapping (y >= 1000)
              false   // NO calibration yet
            );

            // Use raw depth if valid AND within ±1 inch of expected 72"
            // Otherwise fall back to 72" (6 feet)
            const expectedDepth = 72; // 6 feet in inches

            if (rawDepth >= 71 && rawDepth <= 73) {
              // Raw depth is valid and close to expected - use actual measurement
              this.averaged_depth = rawDepth;
              this.append_sentry_message(`Using actual depth: ${rawDepth.toFixed(2)} inches (within ±1" of 72")`);
            } else {
              // Raw depth invalid or outside acceptable range - use fallback
              this.averaged_depth = expectedDepth;
              this.append_sentry_message(`Raw depth ${rawDepth.toFixed(2)}" outside ±1" range of 72", using 72 inches`);
            }
            this.depth_calculation_done = true;
          } else if (!this.depth_calculation_done) {
            // Only log failure once, not every frame
            this.append_sentry_message(`Failed to calculate depth - poses: ${!!poses}, keypoints[${rightAnkleIdx}]: ${poses && poses[0] && poses[0].keypoints ? !!poses[0].keypoints[rightAnkleIdx] : 'N/A'}`);
            // Fallback to default depth
            this.averaged_depth = 72;
            this.depth_calculation_done = true;
          }

          this.assessment_function(poses);
          break;

        // --------------------------- 10) ASSESSMENT IN PROGRESS --------------------------------
        case FunctionalReachStates.assessment_started:
          // Draw patient box for visual reference during assessment (same as ready state)
          this.sit_stand_processing.draw_patient_box();

          // Log assessment start only once, not every frame
          if (!this.assessment_started_logged) {
            this.append_sentry_message(`Assessment started for phase: ${this.current_assessment_phase}`);
            this.assessment_started_logged = true;

            // Belt-and-suspenders: Set flags and hide persistent icon at start of assessment
            // to catch rare race condition where hand raise detection doesn't complete before assessment starts
            ASSESSMENT_STATUSES.start_detected = true;
            ASSESSMENT_STATUSES.in_ready_phase = false;

            const hideIconBtn = document.getElementById("hdnHideHandRaisePersistentIcon");
            if (hideIconBtn) {
              hideIconBtn.click();
            }
          }

          // Capture reference points for all phases if not already set
          if (
            !this.left_reference_point &&
            this.current_assessment_phase ===
              FUNCTIONAL_REACH_ASSESSMENT.Left_Reach
          ) {
            this.arom_left_high_text.textContent = "Left";
            this.arom_left_high_value.innerText = "Reach";
            this.arom_left_low_text.textContent = "0";
            this.arom_left_low_value.innerText = "inches";
          } else if (
            !this.right_reference_point &&
            this.current_assessment_phase ===
              FUNCTIONAL_REACH_ASSESSMENT.Right_Reach
          ) {
            this.arom_right_high_text.textContent = "Right";
            this.arom_right_high_value.innerText = "Reach";
            this.arom_right_low_text.textContent = "0";
            this.arom_right_low_value.innerText = "inches";
          } else if (
            (!this.forward_left_reference_point && !this.forward_right_reference_point) &&
            this.current_assessment_phase ===
              FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach
          ) {
            this.arom_right_high_text.textContent = "Forward";
            this.arom_right_high_value.innerText = "Reach";
            this.arom_right_low_text.textContent = "0";
            this.arom_right_low_value.innerText = "inches";
          }
          if (
            !this.left_reference_point ||
            !this.right_reference_point ||
            (!this.forward_left_reference_point && !this.forward_right_reference_point)
          ) {
            const armStretched = isArmStretched(poses, this.current_assessment_phase);
            if (armStretched) {
              if (
                !this.left_reference_point &&
                this.current_assessment_phase ===
                  FUNCTIONAL_REACH_ASSESSMENT.Left_Reach
              ) {
                this.left_reference_point = this.getReferencePoint(
                  poses,
                  FUNCTIONAL_REACH_ASSESSMENT.Left_Reach,
                );
              } else if (
                !this.right_reference_point &&
                this.current_assessment_phase ===
                  FUNCTIONAL_REACH_ASSESSMENT.Right_Reach
              ) {
                this.right_reference_point = this.getReferencePoint(
                  poses,
                  FUNCTIONAL_REACH_ASSESSMENT.Right_Reach,
                );
              } else if (
                this.current_assessment_phase ===
                  FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach
              ) {
                const keypoints = poses[0].keypoints;
                const isInProfile = isPatientInProfilePosition(keypoints, this.baseline_shoulder_width);

                // this.append_sentry_message(`Forward reach mid-assessment: isInProfile=${isInProfile}`);

                if (isInProfile) {
                  const armStatus = getStretchedArmForForwardReach(poses);
                  // this.append_sentry_message(`Mid-assessment arm status: left=${armStatus.left}, right=${armStatus.right}`);

                  // Priority: If BOTH arms stretched, capture LEFT first
                  if (armStatus.left && armStatus.right) {
                    // this.append_sentry_message(`Both arms stretched - checking left reference`);
                    if (!this.forward_left_reference_point) {
                      this.forward_left_reference_point = this.getReferencePoint(
                        poses,
                        FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
                        'left'
                      );
                      this.append_sentry_message(`Forward LEFT reference captured during assessment (both arms, left priority)`);
                    } else {
                      // this.append_sentry_message(`Forward LEFT reference already exists - skipping`);
                    }
                  } else {
                    // Only one arm stretched - capture whichever one it is
                    if (armStatus.left && !this.forward_left_reference_point) {
                      this.forward_left_reference_point = this.getReferencePoint(
                        poses,
                        FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
                        'left'
                      );
                      this.append_sentry_message(`Forward LEFT reference captured during assessment`);
                    }

                    if (armStatus.right && !this.forward_right_reference_point) {
                      this.forward_right_reference_point = this.getReferencePoint(
                        poses,
                        FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
                        'right'
                      );
                      this.append_sentry_message(`Forward RIGHT reference captured during assessment`);
                    }
                  }
                } else {
                  // this.append_sentry_message(`Not in profile - skipping reference capture`);
                }
              }
            }
          }
          this.assessment_function(poses);

          // LIVE CAMERA: Re-enabled assessment timer logic
          let time_left =
            time_per_assessment_in_sec -
            Math.floor((this.current_time - this.assessment_timer) / 1000);
          if (time_left <= 3 && this.play_beep_sound) {
            this.play_beep_sound = false;
            this.audio_player.play(FunctionalReachAudios.beepSound);
          }
          if (
            this.current_time - this.assessment_timer >=
            time_per_assessment_in_sec * 1000
          ) {
            this.assessment_timer = this.current_time;
            this.completed_phase_count++;
            this.rewind_target = this.current_assessment_phase;
            this.current_state = FunctionalReachStates.post_assessment_modal;
            document.getElementById("hdnAromCompletionMessage").click();
            document.getElementById("btn-arom-assessment-counter").click();
            this.audio_player.play(
              FunctionalReachAudios.phaseCompletion,
              this.current_assessment_phase,
            );
          }

          break;

        // --------------------------- 11) POST-ASSESSMENT --------------------------------
        case FunctionalReachStates.post_assessment_modal:
          this.arom_realtime_angle_left.style.display = "none";
          this.arom_realtime_angle_right.style.display = "none";
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer >= 3000
          ) {
            this.assessment_timer = this.current_time;
            this.play_beep_sound = true;
            this.switch_to_next_sub_assessment_state();
          }
          break;

        // --------------------------- 12) ALL SUB-ASSESSMENTS COMPLETE --------------------------------
        case FunctionalReachStates.all_assessments_complete:
          this.end_time = this.current_time;

          // Switch back to MoveNet multi-pose for next assessment
          this.append_sentry_message("Switching back to MoveNet multi-pose");
          this.switch_to_multi_pose_callback();

          let response = this.build_response();
          this.append_sentry_message(
            "Assessment response:" + JSON.stringify(response),
          );
          if (window["AssessmentPageReference"]) {
            window["AssessmentPageReference"].CompleteAssessment(response);
          } else {
            this.append_sentry_message("CRITICAL ERROR: window['AssessmentPageReference'] not found - assessment results not delivered to UI");
          }
          this.current_state = FunctionalReachStates.test_complete;
          ASSESSMENT_STATUSES.assessment_Completed = true;
          break;

        default:
          break;
      }
    } catch (error) {
      this.append_sentry_message(`CRITICAL ERROR - State: ${this.current_state}, Phase: ${this.current_assessment_phase}, Error: ${error.message || error}`);
    }
  }

  /**
   * Checks if any detected poses have feet within the given bounding box.
   * @param {Array} poses - Array of detected poses.
   * @param {Object} bounding_box - Bounding box with x, y, width, height.
   * @returns {boolean} - True if at least one pose is within the box.
   */
  are_within_bbox(poses, bounding_box) {
    let offset = 0;
    if (!this.sit_stand_processing.is_patient_in_box_status) {
      offset = bounding_box.height;
    }
    for (let pose of poses) {
      const indices = getKeypointIndices(pose.keypoints);

      let leftAnkle = this.camera.scale_to_fit({
        x: pose.keypoints[indices.leftAnkle].x,
        y: pose.keypoints[indices.leftAnkle].y,
      });
      let rightAnkle = this.camera.scale_to_fit({
        x: pose.keypoints[indices.rightAnkle].x,
        y: pose.keypoints[indices.rightAnkle].y,
      });
      if (
        rightAnkle.x > bounding_box.x &&
        leftAnkle.y > bounding_box.y + Math.abs(offset * 0.25) &&
        leftAnkle.x < bounding_box.x + bounding_box.width &&
        rightAnkle.y <
          bounding_box.y + bounding_box.height - Math.abs(offset * 0.25)
      ) {
        this.sit_stand_processing.is_patient_in_box_status = true;
        return true;
      }
    }
    this.sit_stand_processing.is_patient_in_box_status = false;
    return false;
  }

  /**
   * Builds the final assessment response object with summary metrics.
   * @returns {Object} - Assessment result including start/end times and angles.
   */
  build_response() {
    return {
      test_type: "functional_Reach",
      start_time: this.start_time.toString(),
      end_time: this.end_time.toString(),

      phases: [
        {
          maximum_reach: reach_distance_metric.leftReach * 0.0254,
          maximum_reach_time: 0,
          phase_score: reach_distance_metric.leftReach * 0.0254,
          // Skipped with the remote's NEXT; the reach values are then 0
          skipped: this.skipped_phases.includes(
            FUNCTIONAL_REACH_ASSESSMENT.Left_Reach,
          ),
          mode: {
            index: 0,
            code: "left",
          },
        },
        {
          maximum_reach: reach_distance_metric.rightReach * 0.0254,
          maximum_reach_time: 0,
          phase_score: reach_distance_metric.rightReach * 0.0254,
          // Skipped with the remote's NEXT; the reach values are then 0
          skipped: this.skipped_phases.includes(
            FUNCTIONAL_REACH_ASSESSMENT.Right_Reach,
          ),
          mode: {
            index: 1,
            code: "right",
          },
        },
        {
          maximum_reach: reach_distance_metric.forwardReach * 0.0254,
          maximum_reach_time: 0,
          phase_score: reach_distance_metric.forwardReach * 0.0254,
          // Skipped with the remote's NEXT; the reach values are then 0
          skipped: this.skipped_phases.includes(
            FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
          ),
          mode: {
            index: 2,
            code: "forward",
          },
        },
      ],
    };
  }

  /**
   * Identifies the patient pose from multi-person detections.
   * Prefers already-tracked ID or chooses closest feet within patient box.
   * @param {Array} poses - Array of detected poses.
   * @returns {MoveNetPoseWrapper|null} - Wrapped pose of patient or null.
   */
  detect_patient(poses) {
    if (this.patient_id !== -1) {
      for (let person of poses) {
        if (person.score > 0.25 && person.id === this.patient_id) {
          this.patient_ID_changed = false;
          return new MoveNetPoseWrapper(person);
        }
      }
    }
    if (!poses || poses.length === 0) return null;

    let patient_ID = null;
    let therapist_pose = null;
    let min_feet_to_center_distance = Infinity;

    for (let person of poses) {
      if (person.id === this.therapist_id) therapist_pose = person;
      if (person.score > 0.25 && person.id !== this.therapist_id) {
        const indices = getKeypointIndices(person.keypoints);

        let left_ankle = this.camera.scale_to_fit({
          x: person.keypoints[indices.leftAnkle].x,
          y: person.keypoints[indices.leftAnkle].y,
        });
        let right_ankle = this.camera.scale_to_fit({
          x: person.keypoints[indices.rightAnkle].x,
          y: person.keypoints[indices.rightAnkle].y,
        });
        if (
          right_ankle.x > this.sit_stand_processing.patient_box.x &&
          left_ankle.y > this.sit_stand_processing.patient_box.y &&
          left_ankle.x <
            this.sit_stand_processing.patient_box.x +
              this.sit_stand_processing.patient_box.width &&
          right_ankle.y <
            this.sit_stand_processing.patient_box.y +
              this.sit_stand_processing.patient_box.height
        ) {
          let current_feet_to_center_distance = Math.abs(
            (left_ankle.x + right_ankle.x) / 2 -
              (this.sit_stand_processing.patient_box.x +
                (this.sit_stand_processing.patient_box.x +
                  this.sit_stand_processing.patient_box.width)) /
                2,
          );
          if (current_feet_to_center_distance < min_feet_to_center_distance) {
            min_feet_to_center_distance = current_feet_to_center_distance;
            patient_ID = person;
            this.patient_id = person.id;
          }
        }
      }
    }
    if (patient_ID) {
      this.patient_ID_changed = true;
      return new MoveNetPoseWrapper(patient_ID);
    }
    if (therapist_pose) {
      let left_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[15].x,
        y: therapist_pose.keypoints[15].y,
      });
      let right_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[16].x,
        y: therapist_pose.keypoints[16].y,
      });
      if (
        right_ankle.x > this.sit_stand_processing.patient_box.x &&
        left_ankle.y > this.sit_stand_processing.patient_box.y &&
        left_ankle.x <
          this.sit_stand_processing.patient_box.x +
            this.sit_stand_processing.patient_box.width &&
        right_ankle.y <
          this.sit_stand_processing.patient_box.y +
            this.sit_stand_processing.patient_box.height
      ) {
        this.patient_ID_changed = true;
        this.patient_id = therapist_pose.id;
        return new MoveNetPoseWrapper(therapist_pose);
      }
    }
    return null;
  }

  /**
   * After detecting a start gesture, switch to single-pose estimation (e.g., BlazePose)
   * to isolate the main participant. This function also chooses which bounding box
   * to pass to the single-pose model for the best detection.
   * @param {Array} poses - The array of multi-pose keypoints from the current frame.
   */
  switch_to_blazepose(poses) {
    // The therapist's selection wins over the automatic pick (it already passed the start-box check)
    const selected = this.patient_selection.selected_patient();
    let patient = selected ? new MoveNetPoseWrapper(selected) : this.detect_patient(poses);
    if (!patient) {
      this.append_sentry_message("WARNING: Patient not found for BlazePose switch - staying with MoveNet");
      return;
    }

    let seed_rect = this.get_blazepose_bbox(patient);
    // this.append_sentry_message(
    //   "BlazePose seed rect: " + JSON.stringify(seed_rect) +
    //   " | Video dims: " + this.camera.video.videoWidth + "x" + this.camera.video.videoHeight
    // );

    this.switch_to_single_pose_callback(seed_rect);
    this.append_sentry_message("BlazePose switch completed successfully");
  }

  /**
   * Computes the seed bounding box for BlazePose based on patient's hips and spine.
   * @param {MoveNetPoseWrapper} patient - Wrapped patient pose.
   * @returns {Object} - Seed rectangle normalized by video dimensions.
   */
  get_blazepose_bbox(patient) {
    let left_hip = patient.pose.keypoints[11];
    let right_hip = patient.pose.keypoints[12];
    let spine_base = {
      x: (left_hip.x + right_hip.x) / 2,
      y: (left_hip.y + right_hip.y) / 2,
    };
    let bbox = patient.pose.box;
    let box_size = 2 * Math.ceil(bbox.yMax * this.options.sh - spine_base.y);
    // this.append_sentry_message(
    //   "Seed box: " +
    //     JSON.stringify(bbox) +
    //     " spine base: " +
    //     JSON.stringify(spine_base) +
    //     " box size: " +
    //     box_size
    // );
    return {
      xCenter: spine_base.x / this.camera.video.videoWidth,
      yCenter: spine_base.y / this.camera.video.videoHeight,
      width: box_size / this.camera.video.videoWidth,
      height: box_size / this.camera.video.videoHeight,
      rotation: 0.0,
    };
  }

  /**
   * True while the patient can be changed with the remote: before each reach, from the patient box
   * check (or the reach's first instruction) until the start is accepted.
   * @returns {boolean}
   */
  is_patient_selection_open() {
    return (
      [
        FunctionalReachStates.begin_assessment,
        FunctionalReachStates.calibration_1,
        FunctionalReachStates.hand_orientation,
        FunctionalReachStates.hand_orientation_2,
        FunctionalReachStates.readyState,
        FunctionalReachStates.raiseHand,
      ].includes(this.current_state) &&
      !this.voice_command_received &&
      !ASSESSMENT_STATUSES.exit_assessment_modal &&
      !ASSESSMENT_STATUSES.calibration_error_state &&
      !ASSESSMENT_STATUSES.assessment_error
    );
  }

  /**
   * Patient selection from outside the frame loop (BLE remote UP/DOWN): moves the patient to the
   * next detected person on screen. They still have to be in the patient box for the reach to start.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the selection changed.
   */
  cyclePatient(direction, source) {
    if (!this.is_patient_selection_open()) return false;

    const selected = this.patient_selection.cycle(direction);
    if (!selected) return false;

    this.patient_id = selected.id;
    this.append_sentry_message(`Patient selected by ${source}: pose id ${selected.id}`);
    return true;
  }

  /**
   * Start trigger from outside the frame loop (BLE remote START_STOP). Accepted in raiseHand with
   * the selected participant in the box, also while the ready prompt is still playing (it is cut
   * short) and before the 3 s settle time. Starts the current reach phase.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStartTrigger(source) {
    // A remote start needs a participant selected with UP/DOWN (a hand raise or voice start doesn't)
    const patient = this.patient_selection.selected_patient();
    if (
      !patient ||
      this.current_state !== FunctionalReachStates.raiseHand ||
      this.return_to_start_modal_shown ||
      this.voice_command_received ||
      !this.are_within_bbox([patient], this.sit_stand_processing.patient_box)
    ) {
      return false;
    }
    // Same flag as voice START, so update() starts the reach through the existing path (which
    // doesn't wait for the prompt or the 3 s settle time once this is set)
    this.voice_command_received = true;
    // Cut the prompt short: the remote may start while it is still playing
    const audio = this.audio_player.current_playing_audio;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      this.audio_player.current_playing_audio = null;
    }
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.append_sentry_message(`Start triggered by ${source}`);
    return true;
  }

  /**
   * Stop trigger from outside the frame loop (BLE remote START_STOP). Accepted only while a reach
   * phase is running (start triggered, phase not yet finished or errored). Freezes update() so
   * nothing more is measured while the caller shows the exit choice.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStopTrigger(source) {
    const running = [
      FunctionalReachStates.go_state,
      FunctionalReachStates.pre_assessment,
      FunctionalReachStates.assessment_started,
    ].includes(this.current_state);
    if (
      !running ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = true;
    this.append_sentry_message(`Assessment stopped by ${source}`);
    return true;
  }

  /**
   * NEXT trigger from outside the frame loop (BLE remote NEXT). Accepted only while no reach is
   * running:
   *   - during the setup calibration (begin_assessment): leave the assessment (caller exits without a modal)
   *   - during a reach's setup or raise-hand step, not the last reach: skip it and move to the next one
   *   - last reach, no reach measured yet: leave the assessment (caller exits without a modal)
   *   - last reach, some reach measured: skip it and complete, which shows the results
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {"next_phase"|"complete"|"exit"|null} What happened, or null if NEXT doesn't apply now.
   */
  receiveNextTrigger(source) {
    const calibrating =
      this.current_state === FunctionalReachStates.begin_assessment;
    const between_reaches = [
      FunctionalReachStates.calibration_1,
      FunctionalReachStates.hand_orientation,
      FunctionalReachStates.hand_orientation_2,
      FunctionalReachStates.readyState,
      FunctionalReachStates.raiseHand,
    ].includes(this.current_state);
    if (
      !(calibrating || between_reaches) ||
      this.voice_command_received ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return null;
    }

    if (calibrating) {
      ASSESSMENT_STATUSES.exit_assessment_modal = true;
      this.append_sentry_message(`Assessment exited by ${source} (NEXT during calibration)`);
      return "exit";
    }

    const is_last_reach =
      this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach;
    if (is_last_reach && this.completed_phase_count === 0) {
      ASSESSMENT_STATUSES.exit_assessment_modal = true;
      this.append_sentry_message(`Assessment exited by ${source} (NEXT, no reach measured)`);
      return "exit";
    }

    this.skip_current_phase(source);
    if (is_last_reach) {
      this.current_state = FunctionalReachStates.all_assessments_complete;
      return "complete";
    }
    this.switch_to_next_sub_assessment_state();
    return "next_phase";
  }

  // Leaves the current reach without measuring it and records it as skipped
  skip_current_phase(source) {
    this.skipped_phases.push(this.current_assessment_phase);
    this.rewind_target = this.current_assessment_phase;
    // Close this phase's prompts before the transition opens the next phase's own
    document.getElementById("hdnClosePhasePrompts")?.click();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    const audio = this.audio_player.current_playing_audio;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      this.audio_player.current_playing_audio = null;
    }
    this.return_to_start_modal_shown = false;
    this.frames_with_no_patient = 0;
    // Not cleanupUI(): it also clears the baseline shoulder width that Forward Reach relies on
    this.append_sentry_message(
      `Phase ${this.current_assessment_phase} skipped by ${source}`
    );
  }

  /**
   * REWIND trigger from outside the frame loop (BLE remote REWIND). Goes back exactly one reach:
   * redoes the reach that most recently completed or was skipped with NEXT. Active from then until
   * the next reach's start is accepted, and on the assessment-complete modal after the last reach
   * (the caller closes the modal). After a rewind it stays inactive until a reach completes or is
   * skipped again, so it never goes back more than one reach.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @param {boolean} complete_modal_open - Whether the assessment-complete modal is showing.
   * @returns {boolean} True if it rewound.
   */
  receiveRewindTrigger(source, complete_modal_open = false) {
    if (
      this.rewind_target === null ||
      this.voice_command_received ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state ||
      ASSESSMENT_STATUSES.assessment_error
    ) {
      return false;
    }
    const from_complete =
      this.current_state === FunctionalReachStates.test_complete;
    const between_reaches = [
      FunctionalReachStates.post_assessment_modal,
      FunctionalReachStates.calibration_1,
      FunctionalReachStates.hand_orientation,
      FunctionalReachStates.hand_orientation_2,
      FunctionalReachStates.readyState,
      FunctionalReachStates.raiseHand,
    ].includes(this.current_state);
    if (from_complete ? !complete_modal_open : !between_reaches) return false;

    this.rewind_to_phase(this.rewind_target, source);
    return true;
  }

  // Makes `phase` (the one that completed or was skipped) the current reach again and restarts it
  rewind_to_phase(phase, source) {
    if (this.skipped_phases.includes(phase)) {
      this.skipped_phases = this.skipped_phases.filter((p) => p !== phase);
    } else {
      this.completed_phase_count--;
    }
    this.rewind_target = null;

    this.restart_phase(phase, source);
  }

  /**
   * REWIND on the "Incomplete Test" choice after a stop (BLE remote): restarts the reach that was
   * stopped, from its first instruction, keeping the earlier reachs' results. That reach was neither completed nor
   * skipped, so the counts don't change.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if it restarted (only while a stop has the assessment frozen).
   */
  rewindStoppedPhase(source) {
    if (
      !ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.assessment_error ||
      this.current_state === FunctionalReachStates.test_complete
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = false; // unfreeze update()
    this.restart_phase(this.current_assessment_phase, `${source} after a stop`);
    return true;
  }

  /**
   * REWIND on the Assessment Error modal (BLE remote): restarts the reach that errored, from its first
   * instruction, keeping the earlier reachs' results, like the modal's Retry. Only for an error inside a
   * reach (assessment_error is set); a setup failure such as the depth API leaves it unset, and the
   * caller restarts the whole assessment instead.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if it restarted.
   */
  rewindErroredPhase(source) {
    if (!ASSESSMENT_STATUSES.assessment_error) return false;
    this.restart_phase(this.current_assessment_phase, `${source} after an error`);
    return true;
  }

  // Makes `phase` the current reach and restarts it from its first instruction
  restart_phase(phase, source) {
    document.getElementById("hdnClosePhasePrompts")?.click();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    const audio = this.audio_player.current_playing_audio;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      this.audio_player.current_playing_audio = null;
    }
    this.arom_realtime_angle_left.style.display = "none";
    this.arom_realtime_angle_right.style.display = "none";

    const recorders = {
      [FUNCTIONAL_REACH_ASSESSMENT.Left_Reach]: [recordLeftReachDistance, "1"],
      [FUNCTIONAL_REACH_ASSESSMENT.Right_Reach]: [recordRightReachDistance, "2"],
      [FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach]: [recordForwardReachDistance, "3"],
    };
    const [record, phase_no] = recorders[phase];
    this.current_assessment_phase = phase;
    this.assessment_function = (poses) => record(this, poses);
    sessionStorage.setItem("phaseNo", phase_no);
    sessionStorage.setItem("functionalReachAssessmentIndex", phase);

    // The reach may still be on BlazePose (rewound from its result screen); the hand raise needs MoveNet
    this.switch_to_multi_pose_callback();
    this.gesture_detector.reset();
    // Same reset as the error modal's Retry (this reach's result cleared), but from the reach's first
    // instruction instead of readyState, as when the reach starts normally: Right starts at hand_orientation
    this.retryCurrentPhase();
    this.display_modalFaceCamera = false;
    this.current_state =
      phase === FUNCTIONAL_REACH_ASSESSMENT.Right_Reach
        ? FunctionalReachStates.hand_orientation
        : FunctionalReachStates.calibration_1;
    this.append_sentry_message(`Rewound to ${phase} by ${source}`);
  }

  /**
   * Appends timestamped messages to the Sentry log stored in ASSESSMENT_STATUSES.
   * @param {string} message - Message to log.
   */
  append_sentry_message(message) {
    let event = new Date();
    ASSESSMENT_STATUSES.sentry_message += `\n${event.toISOString()}: ${message}`;
    if (message === "Raise your hand to Calibrate.") {
      this.sentry_start_time = Date.now();
      setTimeout(() => {
        if (this.current_state === FunctionalReachStates.with_in_bounds) {
          ASSESSMENT_STATUSES.sentry_message +=
            "\nGesture (Raise Hand) not detected for 30 Seconds";
        }
      }, 30000);
    // } else if (message === "Calibration Completed.") {
    //   let diff = (Date.now() - this.sentry_start_time) / 1000;
    //   ASSESSMENT_STATUSES.sentry_message += `,  Raise Hand Calibration time: ${diff} seconds`;
    }
  }

  /**
   * Advances to the next sub-assessment, resets UI and state,
   * and selects the appropriate next recording function.
   */
  switch_to_next_sub_assessment_state() {
    // this.arom_right_high_value.innerText = "0";
    // this.arom_right_low_value.innerText = "0";
    // this.arom_left_high_value.innerText = "0";
    // this.arom_left_low_value.innerText = "0";

    this.arom_left_high_text.textContent = " ";
    this.arom_left_high_value.innerText = " ";

    this.arom_left_low_text.textContent = 0;
    this.arom_left_low_value.innerText = " ";

    this.arom_right_high_text.textContent = " ";
    this.arom_right_high_value.innerText = " ";

    this.arom_right_low_text.textContent = " ";
    this.arom_right_low_value.innerText = " ";

    // Switch back to MoveNet multi-pose for next hand raise detection
    this.append_sentry_message("Reach complete - switching back to MoveNet for next hand raise detection");
    this.switch_to_multi_pose_callback();
    this.gesture_detector.reset();

    switch (this.current_assessment_phase) {
      case FUNCTIONAL_REACH_ASSESSMENT.Left_Reach:
        this.append_sentry_message(`State transition: Left_Reach complete -> hand_orientation (Right Reach phase)`);

        // Enable right reach
        this.current_assessment_phase = FUNCTIONAL_REACH_ASSESSMENT.Right_Reach;
        this.assessment_function = (poses) =>
          recordRightReachDistance(this, poses);
        sessionStorage.setItem("phaseNo", "2");
        sessionStorage.setItem(
          "functionalReachAssessmentIndex",
          FUNCTIONAL_REACH_ASSESSMENT.Right_Reach,
        );
        // Reset logging flags for new phase (keep depth_calculation_done - reuse calibration)
        this.pre_assessment_logged = false;
        this.assessment_started_logged = false;
        this.display_modalFaceCamera = false;
        this.current_state = FunctionalReachStates.hand_orientation;
        this.assessment_timer = this.current_time;
        break;
        

      case FUNCTIONAL_REACH_ASSESSMENT.Right_Reach:
        // LIVE CAMERA: Switch to forward reach after right
        this.append_sentry_message(`State transition: Right_Reach complete -> calibration_1 (Forward Reach phase)`);

        this.current_assessment_phase = FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach;
        this.assessment_function = (poses) =>
          recordForwardReachDistance(this, poses);
        sessionStorage.setItem("phaseNo", "3");
        sessionStorage.setItem(
          "functionalReachAssessmentIndex",
          FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
        );
        // Reset logging flags for new phase (keep depth_calculation_done - reuse calibration)
        this.pre_assessment_logged = false;
        this.assessment_started_logged = false;
        this.display_modalFaceCamera = false;
        this.current_state = FunctionalReachStates.calibration_1;
        break;

      case FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach:
        this.append_sentry_message(`State transition: Forward_Reach complete -> all_assessments_complete`);
        this.current_state = FunctionalReachStates.all_assessments_complete;
        break;

      default:
        break;
    }
  }

  /**
   * Checks for patient leaving or occlusion errors mid-assessment.
   * If errors exceed thresholds, aborts test and logs error.
   * @param {Array} poses - Array of detected poses.
   */
  assessment_error_check(poses) {
    let pose = this.detect_patient(poses);
    if (!poses || poses.length === 0) {
      this.frames_with_no_patient++;
    } else {
      this.frames_with_no_patient = 0;
      if (pose) {
        let left = this.camera.scale_to_fit({
          x: pose.leftAnkle.x,
          y: pose.leftAnkle.y,
        });
        let right = this.camera.scale_to_fit({
          x: pose.rightAnkle.x,
          y: pose.rightAnkle.y,
        });
        if (
          right.x > this.sit_stand_processing.extended_patient_box.x &&
          left.y > this.sit_stand_processing.extended_patient_box.y &&
          left.x <
            this.sit_stand_processing.extended_patient_box.x +
              this.sit_stand_processing.extended_patient_box.width &&
          right.y <
            this.sit_stand_processing.extended_patient_box.y +
              this.sit_stand_processing.extended_patient_box.height
        ) {
          this.frames_with_patient_outside_extended_box = 0;
        } else {
          this.frames_with_patient_outside_extended_box++;
        }
      }
    }
    // LIVE CAMERA: Increased tolerance for temporary pose detection loss
    if (
      this.frames_with_no_patient >= 30 || // Increased from 15 to 30 frames (~1 second at 30fps)
      this.frames_with_patient_outside_extended_box >= 3
    ) {
      this.end_time = this.current_time;
      let response = this.build_response();
      this.append_sentry_message(
        "Assessment response:" + JSON.stringify(response),
      );
      if (window["AssessmentPageReference"]) {
        window["AssessmentPageReference"].CompleteAssessment(response);
      }
      this.current_state = FunctionalReachStates.test_complete;
      ASSESSMENT_STATUSES.assessment_error = true;
      ASSESSMENT_STATUSES.assessment_Completed = true;
      localStorage.removeItem("modalDisplayed");

      // ALWAYS hide persistent icon and ready message before showing error modal
      const hideIconBtn = document.getElementById("hdnHideHandRaisePersistentIcon");
      if (hideIconBtn) {
        hideIconBtn.click();
      }

      this.audio_player.play(FunctionalReachAudios.assessment_interrupted);
      // Stop the assessment timer
      const timerElement = document.getElementById("btn-arom-assessment-counter");
      if (timerElement) {
        timerElement.click();
      }
      // this.handleAssessmentInterruption(); // Commented to prevent duplicate modal
      // LIVE CAMERA: Modal now triggered automatically by audio player
      this.append_sentry_message(
        "Assessment ERROR occured - Someone has crossed the Patient during assessment or patient has left the Patient region.",
      );
      this.frames_with_no_patient = 0;
      this.frames_with_patient_outside_extended_box = 0;
    }
  }

  /**
   * Handles all assessment interruptions - stops pose detection and timer
   */
  handleAssessmentInterruption() {
    // Check if timer is running BEFORE changing state
    const wasAssessmentRunning = this.current_state === FunctionalReachStates.assessment_started;

    // Stop voice listening (always attempt, regardless of flag state)
    try {
      stopiOSVoiceListening();
    } catch (e) {
      this.append_sentry_message("Error stopping voice listening:", e.message);
    }
    this.enabled_voice_listener = false;

    // Switch back to MoveNet multi-pose
    this.append_sentry_message("Assessment interrupted - switching back to MoveNet");
    this.switch_to_multi_pose_callback();

    // Stop pose detection
    this.current_state = FunctionalReachStates.assessment_interrupted;

    // Handle timer based on whether it was running
    const timerElement = document.getElementById("btn-arom-assessment-counter");
    if (timerElement && wasAssessmentRunning) {
      // Timer is running - click stops AND hides it automatically
      timerElement.click();
    } else if (timerElement) {
      // Timer not running - just hide it manually
      timerElement.style.display = "none";
    }

    // Hide persistent icon before showing error modal
    const hideIconBtn = document.getElementById("hdnHideHandRaisePersistentIcon");
    if (hideIconBtn) {
      hideIconBtn.click();
    }

    // Trigger the error modal with delay to let audio start
    setTimeout(() => {
      const errorBtn = document.getElementById("hdnAssessementErrorPopup");
      if (errorBtn) {
        errorBtn.click();
      }
    }, 250);
  }

  /**
   * Retries the current phase from readyState after ankle check failure
   * Preserves completed phases' data, resets flags for new attempt
   */
  retryCurrentPhase() {
    this.append_sentry_message(`Retrying phase: ${this.current_assessment_phase}`);

    // Reset state to readyState of current phase
    this.current_state = FunctionalReachStates.readyState;
    this.assessment_timer = this.current_time;

    // Reset flags for new attempt
    this.pre_assessment_logged = false;
    this.assessment_started_logged = false;
    this.return_to_start_modal_shown = false;
    this.frames_with_no_patient = 0;

    // Clear ankle reference points AND distance metric for current phase
    if (this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Left_Reach) {
      this.left_reference_point = null;
      this.right_reference_point = null;
      reach_distance_metric.leftReach = 0;
    } else if (this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Right_Reach) {
      this.left_reference_point = null;
      this.right_reference_point = null;
      reach_distance_metric.rightReach = 0;
    } else if (this.current_assessment_phase === FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach) {
      this.forward_left_reference_point = null;
      this.forward_right_reference_point = null;
      reach_distance_metric.forwardReach = 0;
    }

    // Clear error flags
    ASSESSMENT_STATUSES.assessment_error = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;

    // Note: current_assessment_phase stays the same
    // Note: reach_distance_metric for completed phases stays intact (only current phase cleared)
    // Current phase's data will be recalculated from scratch on new attempt
  }

  /**
   * Allows therapist to restart the entire current phase,
   * resetting relevant metrics and UI state.
   */
  restart_phase_feature() {
    if (AromMenuOptions.restartPhase) {
      AromMenuOptions.restartPhase = false;
      // --------------------------- RESTART PHASE --------------------------------
      if (
        this.current_state === FunctionalReachStates.hand_orientation ||
        this.current_state === FunctionalReachStates.hand_orientation_2 ||
        this.current_state === FunctionalReachStates.readyState ||
        this.current_state === FunctionalReachStates.raiseHand ||
        this.current_state === FunctionalReachStates.pre_assessment ||
        this.current_state === FunctionalReachStates.assessment_started ||
        this.current_state === FunctionalReachStates.post_assessment_modal
      ) {
        this.gesture_detector.reset();
        // LIVE CAMERA: Use single cleanup function
        this.cleanupUI();
        if (this.current_state === FunctionalReachStates.assessment_started) {
          document.getElementById("btn-arom-assessment-counter").click();
        }
        if (
          this.current_assessment_phase ===
          FUNCTIONAL_REACH_ASSESSMENT.Left_Reach
        ) {
          this.assessment_function = (poses) =>
            recordLeftReachDistance(this, poses);
          sessionStorage.setItem("phaseNo", "1");
          sessionStorage.setItem(
            "functionalReachAssessmentIndex",
            FUNCTIONAL_REACH_ASSESSMENT.Left_Reach,
          );
          this.current_state = FunctionalReachStates.readyState;
          // document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
          reach_distance_metric.leftReach = 0;
        } else if (
          this.current_assessment_phase ===
          FUNCTIONAL_REACH_ASSESSMENT.Right_Reach
        ) {
          this.current_assessment_phase =
            FUNCTIONAL_REACH_ASSESSMENT.Right_Reach;
          this.assessment_function = (poses) =>
            recordRightReachDistance(this, poses);
          sessionStorage.setItem("phaseNo", "2");
          sessionStorage.setItem(
            "functionalReachAssessmentIndex",
            FUNCTIONAL_REACH_ASSESSMENT.Right_Reach,
          );
          this.current_state = FunctionalReachStates.readyState;
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;

          reach_distance_metric.rightReach = 0;
        } else if (
          this.current_assessment_phase ===
          FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach
        ) {
          this.current_assessment_phase =
            FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach;
          this.assessment_function = (poses) =>
            recordForwardReachDistance(this, poses);
          sessionStorage.setItem("phaseNo", "3");
          sessionStorage.setItem(
            "functionalReachAssessmentIndex",
            FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
          );
          this.current_state = FunctionalReachStates.readyState;
          // document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;

          reach_distance_metric.forwardReach = 0;
          this.forward_left_reference_point = null;
          this.forward_right_reference_point = null;
        }
      }
    }
  }

  /**
   * Allows therapist to skip the current phase,
   * advancing directly to the next stage.
   */
  skip_phase_feature() {
    if (AromMenuOptions.skipPhase) {
      AromMenuOptions.skipPhase = false;
      this.gesture_detector.reset();
      // --------------------------- SKIP PHASE --------------------------------
      if (
        this.current_state === FunctionalReachStates.hand_orientation ||
        this.current_state === FunctionalReachStates.hand_orientation_2 ||
        this.current_state === FunctionalReachStates.readyState ||
        this.current_state === FunctionalReachStates.raiseHand ||
        this.current_state === FunctionalReachStates.pre_assessment ||
        this.current_state === FunctionalReachStates.assessment_started ||
        this.current_state === FunctionalReachStates.post_assessment_modal
      ) {
        // LIVE CAMERA: Use single cleanup function
        this.cleanupUI();

        if (this.current_state === FunctionalReachStates.assessment_started) {
          document.getElementById("btn-arom-assessment-counter").click();
        }
        if (
          this.current_assessment_phase ===
          FUNCTIONAL_REACH_ASSESSMENT.Left_Reach
        ) {
          // skip straight to right‑reach
          this.current_assessment_phase =
            FUNCTIONAL_REACH_ASSESSMENT.Right_Reach;
          this.assessment_function = (poses) =>
            recordRightReachDistance(this, poses);
          sessionStorage.setItem("phaseNo", "2");
          sessionStorage.setItem(
            "functionalReachAssessmentIndex",
            FUNCTIONAL_REACH_ASSESSMENT.Right_Reach,
          );
          this.current_state = FunctionalReachStates.readyState;
          // document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else if (
          [FUNCTIONAL_REACH_ASSESSMENT.Right_Reach].includes(
            this.current_assessment_phase,
          )
        ) {
          // skip to forward-reach
          this.current_assessment_phase =
            FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach;
          this.assessment_function = (poses) =>
            recordForwardReachDistance(this, poses);
          sessionStorage.setItem("phaseNo", "3");
          sessionStorage.setItem(
            "functionalReachAssessmentIndex",
            FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach,
          );
          this.current_state = FunctionalReachStates.readyState;
          // document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else {
          // if already on last assessment, mark complete
          this.current_state = FunctionalReachStates.all_assessments_complete;
        }
      }
    }
  }

  async uploadImageToS3AndCallAPI() {
    try {
      let PatientId = localStorage.getItem("patientServerID");
      let image_name = `images/scene_depth_input_${PatientId}.png`;
      const s3Bucket = S3_BUCKET;
      const s3Key = `depths/scene_depth_${PatientId}.npy`;

      // Capture current frame to offscreen canvas
      this.offscreen_canvas.width = this.camera.video.videoWidth;
      this.offscreen_canvas.height = this.camera.video.videoHeight;
      this.offscreen_canvas_ctx.drawImage(
        this.camera.video,
        0,
        0,
        this.camera.video.videoWidth,
        this.camera.video.videoHeight
      );

      const imageUrl = this.offscreen_canvas.toDataURL("image/png");

      // Convert base64 to binary
      const base64Data = imageUrl.replace(/^data:image\/png;base64,/, "");
      const binaryData = atob(base64Data);
      const binaryBuffer = new Uint8Array(binaryData.length);

      for (let i = 0; i < binaryData.length; i++) {
        binaryBuffer[i] = binaryData.charCodeAt(i);
      }

      // Upload the screenshot to S3
      const params = {
        Bucket: s3Bucket,
        Key: image_name,
        Body: binaryBuffer,
        ContentEncoding: "base64",
        ContentType: "image/png",
      };
      await s3Client.send(new PutObjectCommand(params));
      this.append_sentry_message("Successfully uploaded data to S3");

      const apiURL = API_URL;
      const postData = {
        image_path: image_name,
        model_selection: "vit-large",
        PatientId: PatientId,
        fx: 740,
        fy: 740,
      };

      // Retry logic for external API call
      let attempts = 3;
      while (attempts > 0) {
        try {
          const controller = new AbortController();
          const id = setTimeout(() => controller.abort(), 15000); // 15s timeout

          const response = await fetch(apiURL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(postData),
            signal: controller.signal,
          });

          clearTimeout(id);

          if (!response.ok) {
            this.append_sentry_message(
              `HTTP error! Status: ${response.status}`
            );
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          this.append_sentry_message("API call successful");

          // Load the generated .npy file from S3 into calibration
          this.is_uploaded = await this.calibration.get_depth_data(
            s3Bucket,
            s3Key
          );
          break;
        } catch (error) {
          this.append_sentry_message("Error in API call");
          attempts--;
          if (attempts === 0) {
            this.append_sentry_message(
              "API request failed after multiple attempts."
            );
            return;
          } else {
            this.append_sentry_message(
              `Retrying... attempts left: ${attempts}`
            );
          }
        }
      }
    } catch (err) {
      this.append_sentry_message("Error uploading data to S3 bucket");
      return;
    }
  }

  // Returns the reference point for the given phase
  // armSide parameter: 'left' or 'right' (only used for Forward_Reach)
  getReferencePoint(poses, phase, armSide = null) {
    const keypoints = poses[0].keypoints;
    const indices = getKeypointIndices(keypoints);

    if (phase === FUNCTIONAL_REACH_ASSESSMENT.Left_Reach) {
      return {
        leftWrist: { x: keypoints[indices.leftWrist].x, y: keypoints[indices.leftWrist].y },
        leftAnkle: { x: keypoints[indices.leftAnkle].x, y: keypoints[indices.leftAnkle].y },
        rightAnkle: { x: keypoints[indices.rightAnkle].x, y: keypoints[indices.rightAnkle].y },
      };
    } else if (phase === FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach) {
      // Capture only the specified arm's wrist
      if (armSide === 'left') {
        return {
          leftWrist: { x: keypoints[indices.leftWrist].x, y: keypoints[indices.leftWrist].y },
          leftAnkle: { x: keypoints[indices.leftAnkle].x, y: keypoints[indices.leftAnkle].y },
          rightAnkle: { x: keypoints[indices.rightAnkle].x, y: keypoints[indices.rightAnkle].y },
        };
      } else if (armSide === 'right') {
        return {
          rightWrist: { x: keypoints[indices.rightWrist].x, y: keypoints[indices.rightWrist].y },
          leftAnkle: { x: keypoints[indices.leftAnkle].x, y: keypoints[indices.leftAnkle].y },
          rightAnkle: { x: keypoints[indices.rightAnkle].x, y: keypoints[indices.rightAnkle].y },
        };
      }

      // Fallback: if no armSide specified, capture both (backward compatibility)
      return {
        leftWrist: { x: keypoints[indices.leftWrist].x, y: keypoints[indices.leftWrist].y },
        rightWrist: { x: keypoints[indices.rightWrist].x, y: keypoints[indices.rightWrist].y },
        leftAnkle: { x: keypoints[indices.leftAnkle].x, y: keypoints[indices.leftAnkle].y },
        rightAnkle: { x: keypoints[indices.rightAnkle].x, y: keypoints[indices.rightAnkle].y },
      };
    } else if (phase === FUNCTIONAL_REACH_ASSESSMENT.Right_Reach) {
      return {
        rightWrist: { x: keypoints[indices.rightWrist].x, y: keypoints[indices.rightWrist].y },
        leftAnkle: { x: keypoints[indices.leftAnkle].x, y: keypoints[indices.leftAnkle].y },
        rightAnkle: { x: keypoints[indices.rightAnkle].x, y: keypoints[indices.rightAnkle].y },
      };
    }
    return null;
  }

  /**
   * LIVE CAMERA: Single cleanup function for all UI elements
   */
  cleanupUI() {
    // Clear all floating value displays
    this.arom_left_high_text.textContent = " ";
    this.arom_left_high_value.innerText = " ";
    this.arom_left_low_text.textContent = " ";
    this.arom_left_low_value.innerText = " ";
    this.arom_right_high_text.textContent = " ";
    this.arom_right_high_value.innerText = " ";
    this.arom_right_low_text.textContent = " ";
    this.arom_right_low_value.innerText = " ";

    // Hide realtime angle displays
    this.arom_realtime_angle_left.style.display = "none";
    this.arom_realtime_angle_right.style.display = "none";

    // Hide timer slider
    const timerElement = document.getElementById("btn-arom-assessment-counter");
    if (timerElement) {
      timerElement.style.display = "none";
    }

    // Reset baseline shoulder width for next assessment
    this.baseline_shoulder_width = null;

  }

  /**
   * Capture baseline shoulder width from poses (frontal position)
   * @param {Array} poses - Array of detected poses
   * @param {string} context - Context where capture is attempted (for logging)
   * @returns {boolean} - True if capture successful, false otherwise
   */
  captureBaselineShoulderWidth(poses, context = "unknown") {
    if (this.baseline_shoulder_width) return false; // Already captured

    if (!poses || poses.length === 0) return false;

    // Use closest to center for multi-pose scenarios
    let selectedPose = poses[0];
    if (poses.length > 1) {
      const frameCenterX = 960;
      const frameCenterY = 540;
      let minDistance = Infinity;

      poses.forEach((pose) => {
        if (pose && pose.keypoints) {
          const leftHip = pose.keypoints[11];
          const rightHip = pose.keypoints[12];
          if (leftHip && rightHip) {
            const hipCenterX = (leftHip.x + rightHip.x) / 2;
            const hipCenterY = (leftHip.y + rightHip.y) / 2;
            const distance = Math.sqrt(
              Math.pow(hipCenterX - frameCenterX, 2) +
              Math.pow(hipCenterY - frameCenterY, 2)
            );
            if (distance < minDistance) {
              minDistance = distance;
              selectedPose = pose;
            }
          }
        }
      });
    }

    // Capture from selected pose
    if (!selectedPose || !selectedPose.keypoints) return false;

    const leftShoulder = selectedPose.keypoints[5];   // MoveNet left shoulder
    const rightShoulder = selectedPose.keypoints[6];  // MoveNet right shoulder

    if (leftShoulder && rightShoulder) {
      this.baseline_shoulder_width = Math.abs(leftShoulder.x - rightShoulder.x);
      this.append_sentry_message(`Baseline captured in ${context}: ${this.baseline_shoulder_width.toFixed(2)}px (from ${poses.length} pose${poses.length > 1 ? 's' : ''})`);
      return true;
    } else {
      this.append_sentry_message(`Failed baseline in ${context} - leftShoulder: ${!!leftShoulder}, rightShoulder: ${!!rightShoulder}`);
      return false;
    }
  }

}

export default FunctionalReachAssessment;

export async function functionalReachSentryLogging() {
  SentryLogging("Functional Reach Assessment");
}
